import type { RegistryFamilyProjection } from '@/models/registry'

/**
 * MYAC-1630 — resolve a stored registry reference: THE one way, for every
 * front reader. Same semantics as core_models_services
 * `src/services/registry/references.py` (`FamilyReferences`).
 *
 * A registry family's doc id is ONE of its `identifiers`, not its identity:
 * a merge or a reidentify leaves a tombstone (`merged_into`) and the live
 * family carries the old id in `identifiers`. Stored references are never
 * rewritten, so a row's `registry_ref.family_id`, a revision's `successor`, a
 * report card, a suggestion may all hold an older id; a pinned `revision_key`
 * may be one of an edition's alias `identifiers`. Two references name the
 * same family only when they RESOLVE to the same live family, and the same
 * edition only when they also resolve to the same canonical key. Never
 * compare the stored strings.
 *
 * Pure: every function takes a `lookup(id)` that returns the LIVE family
 * projection an id names (or null/undefined). In the app that is the
 * `registryFamilies` store, which resolves an id through
 * `array-contains-any` on `identifiers` (the browser never receives a
 * tombstone: it is not exposable); the store exposes these same functions
 * bound to its cache. Callers resolve the keys first, as they already do for
 * `familyFor`.
 */

export type RegistryLookup = (familyId: string) => RegistryFamilyProjection | null | undefined

/** An edition reference: `[family_id, revision_key]`. */
export type EditionRef = readonly [string | null | undefined, string | null | undefined]

/** A family's `identifiers`. Tolerant reader: the Firestore read boundary
 *  always sets them, projections built elsewhere (tests, older fixtures) may not. */
function idsOf(family: RegistryFamilyProjection): string[] {
  return family.identifiers ?? []
}

/** The live family `familyId` names, or null (blank, unknown, not yet resolved). */
export function resolveFamily(
  lookup: RegistryLookup,
  familyId: string | null | undefined,
): RegistryFamilyProjection | null {
  if (!familyId) return null
  return lookup(familyId) ?? null
}

/** The live family's id, or null. */
export function liveFamilyId(
  lookup: RegistryLookup,
  familyId: string | null | undefined,
): string | null {
  return resolveFamily(lookup, familyId)?.family_id ?? null
}

/** `a` and `b` name the same family. Equal non-blank strings always do (even
 *  unresolved: the same reference); otherwise both must resolve to one live
 *  family. Blank never matches.
 *  Browser-only nuance: the cache answers only for ids it was asked. When one
 *  side is not resolved, an id the other side's live family carries in
 *  `identifiers` IS that family (resolving it would return exactly that), so
 *  the answer is the backend's once both are resolved. */
export function sameFamily(
  lookup: RegistryLookup,
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false
  if (a === b) return true
  return sharedFamily(lookup, a, b) !== null
}

/** The live family `a` and `b` both name, or null (see `sameFamily`). */
function sharedFamily(
  lookup: RegistryLookup,
  a: string,
  b: string,
): RegistryFamilyProjection | null {
  const familyA = resolveFamily(lookup, a)
  const familyB = resolveFamily(lookup, b)
  if (familyA && familyB) return familyA.family_id === familyB.family_id ? familyA : null
  if (familyA) return idsOf(familyA).includes(b) ? familyA : null
  if (familyB) return idsOf(familyB).includes(a) ? familyB : null
  return null
}

/** Every id the family `familyId` names may be stored under (the id itself,
 *  its live id and all its `identifiers`), for a membership test against
 *  references taken as they are stored. An unresolved id is just itself. */
export function identitySet(
  lookup: RegistryLookup,
  familyId: string | null | undefined,
): ReadonlySet<string> {
  if (!familyId) return new Set()
  const family = resolveFamily(lookup, familyId)
  if (!family) return new Set([familyId])
  return new Set([familyId, family.family_id, ...idsOf(family)])
}

/** The canonical `revisions` key `key` names in `family`: the key itself when
 *  it is one, else the revision whose `identifiers` hold it; null when no
 *  edition holds it (or either side is missing). */
export function canonicalRevisionKey(
  family: RegistryFamilyProjection | null | undefined,
  key: string | null | undefined,
): string | null {
  if (!key || !family) return null
  if (key in family.revisions) return key
  for (const [canonical, revision] of Object.entries(family.revisions)) {
    // Tolerant reader: projections built outside the parser may lack `identifiers`.
    if ((revision.identifiers ?? []).includes(key)) return canonical
  }
  return null
}

/** The canonical key `key` names in the live family `familyId` names, or null. */
export function canonicalKey(
  lookup: RegistryLookup,
  familyId: string | null | undefined,
  key: string | null | undefined,
): string | null {
  return canonicalRevisionKey(resolveFamily(lookup, familyId), key)
}

/** Two `[family_id, revision_key]` pairs name the same edition: the same
 *  family, and keys that resolve to one canonical key. A key no edition holds
 *  compares as itself (a dangling pin is still equal to itself). */
export function sameEdition(lookup: RegistryLookup, a: EditionRef, b: EditionRef): boolean {
  const [familyA, keyA] = a
  const [familyB, keyB] = b
  if (!familyA || !familyB) return false
  if (!sameFamily(lookup, familyA, familyB)) return false
  if ((keyA ?? null) === (keyB ?? null)) return true
  if (!keyA || !keyB) return false
  // One family: both keys are read in it (whichever side is resolved).
  const family =
    familyA === familyB ? resolveFamily(lookup, familyA) : sharedFamily(lookup, familyA, familyB)
  const canonicalA = canonicalRevisionKey(family, keyA) ?? keyA
  const canonicalB = canonicalRevisionKey(family, keyB) ?? keyB
  return canonicalA === canonicalB
}

/** A lookup over projections already in hand (a report, a test): each family
 *  answers for every id in its `identifiers`, as the store does. */
export function lookupOver(families: Iterable<RegistryFamilyProjection>): RegistryLookup {
  const byId = new Map<string, RegistryFamilyProjection>()
  for (const family of families) {
    for (const id of [family.family_id, ...idsOf(family)])
      if (!byId.has(id)) byId.set(id, family)
  }
  return (id) => byId.get(id) ?? null
}

/** Authority keys whose display is not their upper-cased key: a joint
 *  authority, and national marks shown as the body that publishes them. */
const AUTHORITY_LABELS: Readonly<Record<string, string>> = {
  iso_iec: 'ISO/IEC',
  nf: 'AFNOR',
  din: 'DIN',
  bs: 'BSI',
  une: 'UNE',
  uni: 'UNI',
  nen: 'NEN',
  sn: 'SNV',
  ds: 'DS',
}

/** The normative body to show (and write into a new row's `normative_body`)
 *  for a registry `authority` key. Anything not mapped is upper-cased. */
export function authorityLabel(key: string | null | undefined): string {
  if (!key) return ''
  return AUTHORITY_LABELS[key] ?? key.toUpperCase()
}
